import { useState, useCallback } from 'react';
import { useStore } from '../store/useStore';
import { ProcessingStep, AnalysisProgress } from '../types/analysis';
import { uploadFile } from '../services/firebase/storage';
import { submitFinalAnalysis, refineGeminiAnalysis, ChatSession } from '../services/api/documentAnalysis';
import { getClarifyingQuestions, startPreAnalysis } from '../services/api/backend';
import { roles } from '../config/roles';
import { convertToPdf } from '../services/files/pdfConverter';
import { fileToBase64 } from '../services/files/fileConverter';

const INITIAL_STEPS: ProcessingStep[] = [
  {
    id: 'waiting',
    title: 'Aguardando Arquivo',
    description: 'Selecione um arquivo para iniciar',
    status: 'pending'
  },
  {
    id: 'upload',
    title: 'Upload do Arquivo',
    description: 'Enviando arquivo para processamento',
    status: 'pending'
  },
  {
    id: 'questions',
    title: 'Perguntas Esclarecedoras',
    description: 'Respondendo perguntas sobre o objetivo',
    status: 'pending'
  },
  {
    id: 'preanalyze',
    title: 'Pré-Análise',
    description: 'Analisando o documento com IA',
    status: 'pending'
  },
  {
    id: 'complete',
    title: 'Parecer Finalizado',
    description: 'Processamento concluído com sucesso',
    status: 'pending'
  }
];

export function useAnalysisProcess() {
  const { user, addAnalysis } = useStore();
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [analysis, setAnalysis] = useState<string | null>(null);
  const [currentStep, setCurrentStep] = useState(0);
  const [steps, setSteps] = useState<ProcessingStep[]>(INITIAL_STEPS);
  const [progress, setProgress] = useState<AnalysisProgress[]>([]);

  // States for interactive flow
  const [preAnalysis, setPreAnalysis] = useState<string | null>(null);
  const [showPreAnalysisModal, setShowPreAnalysisModal] = useState(false);
  const [chatSession, setChatSession] = useState<ChatSession | null>(null);
  const [pendingData, setPendingData] = useState<{
    base64Content: string;
    mimeType: string;
    role: any;
    matter: string;
    downloadUrl: string;
    storagePath: string;
    file: File;
    sessionId?: string;
    userAnswers?: string[];
  } | null>(null);
  const [isRefining, setIsRefining] = useState(false);

  // States for clarifying questions
  const [clarifyingQuestions, setClarifyingQuestions] = useState<string[]>([]);
  const [showQuestionsModal, setShowQuestionsModal] = useState(false);
  const [isSubmittingAnswers, setIsSubmittingAnswers] = useState(false);
  const [questionsError, setQuestionsError] = useState<string | null>(null);

  const updateStep = useCallback((index: number, status: ProcessingStep['status']) => {
    setSteps(prev => prev.map((step, i) =>
      i === index ? { ...step, status } : step
    ));
  }, []);

  const resetAnalysis = useCallback(() => {
    setIsProcessing(false);
    setError(null);
    setAnalysis(null);
    setCurrentStep(0);
    setSteps(INITIAL_STEPS);
    setProgress([]);
    setPreAnalysis(null);
    setShowPreAnalysisModal(false);
    setChatSession(null);
    setPendingData(null);
    // Reset clarifying questions states
    setClarifyingQuestions([]);
    setShowQuestionsModal(false);
    setIsSubmittingAnswers(false);
    setQuestionsError(null);
  }, []);

  /**
   * Inicia o processo de análise:
   * 1. Faz upload do arquivo
   * 2. Exibe as perguntas esclarecedoras (ANTES da pré-análise)
   */
  const processAnalysis = async (file: File, roleId: string, matter: string) => {
    if (!file || !roleId || !user) {
      setError('Dados inválidos para análise');
      return;
    }

    const role = roles.find(r => r.id === roleId);
    if (!role) {
      setError('Papel não encontrado');
      return;
    }

    try {
      setIsProcessing(true);
      setCurrentStep(1);
      setProgress([]);
      setError(null);

      // Step 1: Upload (Firebase)
      updateStep(1, 'processing');
      const { downloadUrl, storagePath } = await uploadFile(file, user.id);
      updateStep(1, 'completed');

      // Preparar arquivo para uso posterior
      const pdfFile = await convertToPdf(file);
      const base64Content = await fileToBase64(pdfFile);
      const mimeType = pdfFile.type;

      // Armazenar dados para uso posterior
      setPendingData({
        base64Content,
        mimeType,
        role,
        matter,
        downloadUrl,
        storagePath,
        file,
      });

      // Step 2: Mostrar perguntas esclarecedoras ANTES da pré-análise
      setCurrentStep(2);
      updateStep(2, 'processing');

      console.log('Fetching clarifying questions...');

      // Buscar perguntas pré-definidas
      const questionsResult = await getClarifyingQuestions();
      console.log('Questions received:', questionsResult);

      setClarifyingQuestions(questionsResult.questions);
      console.log('Setting showQuestionsModal to true');
      setShowQuestionsModal(true);

    } catch (error: any) {
      console.error('Error processing document:', error);
      setError(error.message || 'Erro ao processar o documento');
      updateStep(currentStep, 'error');
      setIsProcessing(false);
    }
  };

  /**
   * Recebe as respostas das perguntas e continua para pré-análise
   */
  const submitAnswersAndContinue = async (answers: string[]) => {
    if (!pendingData || !user) return;

    try {
      setIsSubmittingAnswers(true);
      setQuestionsError(null);

      // Fechar modal
      setShowQuestionsModal(false);
      updateStep(2, 'completed');

      // Salvar respostas para incluir no contexto
      setPendingData(prev => prev ? { ...prev, userAnswers: answers } : null);

      // Step 3: Pré-análise
      setCurrentStep(3);
      updateStep(3, 'processing');

      // Determinar papel completo
      let papel = pendingData.role.name;
      if (pendingData.role.name === "Parecerista Jurídico" || pendingData.role.name === "Controlador Interno") {
        papel += " " + pendingData.matter;
      }

      // Iniciar pré-análise no backend enviando as respostas
      const preAnalysisResult = await startPreAnalysis(
        pendingData.base64Content,
        pendingData.mimeType,
        papel,
        pendingData.matter,
        answers // Passando as respostas aqui
      );

      // Armazenar sessionId para uso posterior
      setPendingData(prev => prev ? {
        ...prev,
        sessionId: preAnalysisResult.sessionId,
        userAnswers: answers
      } : null);

      setPreAnalysis(preAnalysisResult.preAnalysis);
      setIsSubmittingAnswers(false);
      setShowPreAnalysisModal(true);

    } catch (error: any) {
      console.error('Error in pre-analysis:', error);
      setQuestionsError(error.message || 'Erro ao processar');
      setIsSubmittingAnswers(false);
      setError(error.message || 'Erro na pré-análise');
      updateStep(currentStep, 'error');
    }
  };

  /**
   * Confirma a pré-análise e gera o parecer final
   */
  const confirmAnalysis = async () => {
    if (!pendingData || !preAnalysis || !user) {
      console.error('Missing required data for confirmAnalysis');
      return;
    }

    try {
      setIsRefining(true);
      setShowPreAnalysisModal(false);
      updateStep(3, 'completed');

      // Step 4: Gerar parecer final
      setCurrentStep(4);
      updateStep(4, 'processing');

      // Garantir que matter tenha um valor (webhook exige 'assunto')
      const matterValue = pendingData.matter || 'geral';

      const { analysis: analysisResult, ementa } = await submitFinalAnalysis(
        pendingData.base64Content,
        pendingData.sessionId || '',
        pendingData.role,
        matterValue,
        preAnalysis
      );

      setAnalysis(analysisResult);
      setIsProcessing(false);
      setIsRefining(false);

      // Salvar em background
      addAnalysis({
        fileName: pendingData.file.name,
        roleId: pendingData.role.id,
        analysis: analysisResult,
        ementa,
        createdAt: new Date(),
        userId: user.id,
        fileUrl: pendingData.downloadUrl,
        storagePath: pendingData.storagePath
      }).then(() => {
        updateStep(4, 'completed');
      }).catch((err) => {
        console.error('Error saving analysis:', err);
      });

    } catch (error: any) {
      console.error('Error finalizing document:', error);
      setError(error.message || 'Erro ao finalizar documento');
      updateStep(currentStep, 'error');
      setIsProcessing(false);
      setIsRefining(false);
    }
  };

  const refineAnalysis = async (feedback: string) => {
    if (!chatSession) return;

    try {
      setIsRefining(true);
      const { text, chatSession: newSession } = await refineGeminiAnalysis(chatSession, feedback);
      setPreAnalysis(text);
      setChatSession(newSession);
    } catch (error: any) {
      console.error('Error refining analysis:', error);
      alert("Erro ao refinar análise: " + error.message);
    } finally {
      setIsRefining(false);
    }
  };

  return {
    isProcessing,
    error,
    analysis,
    currentStep,
    steps,
    progress,
    processAnalysis,
    updateStep,
    setAnalysis,
    resetAnalysis,
    // Pre-analysis exports
    preAnalysis,
    showPreAnalysisModal,
    confirmAnalysis,
    refineAnalysis,
    setShowPreAnalysisModal,
    isRefining,
    // Clarifying questions exports
    clarifyingQuestions,
    showQuestionsModal,
    setShowQuestionsModal,
    isSubmittingAnswers,
    questionsError,
    submitAnswersAndContinue,
  };
}
